import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ClipboardList, CircleCheck, AlertOctagon, BookMarked, Zap, ArrowUpRight,
  FileText, Sparkles,
} from 'lucide-react';
import { getHistory, getUser } from '../../utils/api';

export default function Overview() {
  const [checks, setChecks] = useState([]);
  const [loading, setLoading] = useState(true);
  const user = getUser();

  useEffect(() => {
    getHistory()
      .then(setChecks)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  // Compute stats from real data
  const totalChecks = checks.length;
  const compliantChecks = checks.filter(c => c.compliant).length;
  const totalGaps = checks.reduce((sum, c) =>
    sum + (c.results_json || []).filter(r => r.outcome === 'fail').length, 0);
  const avgScore = checks.length > 0
    ? (checks.reduce((sum, c) => sum + parseFloat(c.overall_score || 0), 0) / checks.length).toFixed(0)
    : 0;

  const stats = [
    { key: 'total', icon: ClipboardList, tone: 'indigo', value: totalChecks, label: 'Total checks run', hint: 'All compliance evaluations' },
    { key: 'compliant', icon: CircleCheck, tone: 'emerald', value: compliantChecks, label: 'Compliant outcomes', hint: 'Fully compliant checks' },
    { key: 'gaps', icon: AlertOctagon, tone: 'rose', value: totalGaps, label: 'Total gaps identified', hint: 'Across all checks' },
    { key: 'score', icon: BookMarked, tone: 'mint', value: `${avgScore}%`, label: 'Average compliance score', hint: 'Across all frameworks' },
  ];

  const toneMap = {
    indigo: 'bg-[#EEEEF7] text-[#3D3D8A]',
    emerald: 'bg-[#E8F3EE] text-[#1B6F5E]',
    rose: 'bg-[#FBEAEA] text-[#A4243B]',
    mint: 'bg-[#E8F3EE] text-[#1B6F5E]',
  };

  const recent = checks.slice(0, 4);

  return (
    <div className="max-w-[1280px]">
      {/* Heading */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
        <div>
          <div className="text-[11.5px] tracking-[0.16em] uppercase text-[#1B6F5E] font-medium">
            Welcome back{user?.first_name ? `, ${user.first_name}` : ''}
          </div>
          <h2 className="mt-2 font-display text-[36px] lg:text-[44px] leading-[1.05] text-[#0B1F2D]">
            Compliance <span className="italic">overview.</span>
          </h2>
          <p className="mt-3 text-[14.5px] leading-[1.55] text-[#0B1F2D]/65 max-w-[640px]">
            Your ESGine® compliance dashboard, powered by ESG-as-Code®. Every outcome maps to a regulatory clause.
          </p>
        </div>
        <Link
          to="/dashboard/run"
          className="inline-flex items-center gap-2 h-11 px-5 rounded-full bg-[#0B1F2D] text-[#FAFAF5] hover:bg-[#0B1F2D]/90 text-[13px] font-medium"
        >
          <Zap className="w-4 h-4" />
          Run new check
        </Link>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-5">
        {stats.map((s) => (
          <div key={s.key} className="card-lift rounded-2xl border border-[#E8E5DC] bg-white p-6">
            <div className="flex items-start justify-between">
              <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${toneMap[s.tone]}`}>
                <s.icon className="w-5 h-5" />
              </div>
              <div className="font-mono-tight text-[10.5px] text-[#0B1F2D]/40">/{s.key}</div>
            </div>
            <div className="mt-6 font-display text-[44px] leading-none text-[#0B1F2D]">
              {loading ? '—' : s.value}
            </div>
            <div className="mt-2 text-[13px] text-[#0B1F2D] font-medium">{s.label}</div>
            <div className="mt-1 text-[11.5px] text-[#0B1F2D]/55">{s.hint}</div>
          </div>
        ))}
      </div>

      {/* Big CTA banner */}
      <div className="mt-8 relative overflow-hidden rounded-[22px] border border-[#0B1F2D] bg-[#0B1F2D] text-[#FAFAF5]">
        <div className="absolute inset-0 grain opacity-25" />
        <div className="absolute -top-20 -right-20 w-[360px] h-[360px] rounded-full bg-[#1B6F5E]/20 blur-3xl" />
        <div className="relative p-8 lg:p-10 grid grid-cols-1 lg:grid-cols-12 gap-6 items-center">
          <div className="lg:col-span-8">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/15 bg-white/[0.04] text-[11px] tracking-[0.12em] uppercase text-[#7BC2B0]">
              <Sparkles className="w-3 h-3" /> ESG-as-Code® Engine
            </div>
            <h3 className="mt-5 font-display text-[34px] lg:text-[44px] leading-[1.02]">
              Deterministic. Auditable.{' '}
              <span className="italic text-[#7BC2B0]">Sovereign.</span>
            </h3>
            <p className="mt-4 text-[14.5px] leading-[1.6] text-[#FAFAF5]/70 max-w-[560px]">
              Upload your ESG document. The engine extracts structured data, evaluates every applicable rule, and maps
              each outcome to its regulatory clause.
            </p>
          </div>
          <div className="lg:col-span-4 flex justify-start lg:justify-end">
            <Link
              to="/dashboard/run"
              className="inline-flex items-center gap-2 h-12 px-6 rounded-full bg-[#FAFAF5] text-[#0B1F2D] hover:bg-[#7BC2B0] text-[13.5px] font-medium transition-colors"
            >
              Start a check <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </div>

      {/* Recent checks */}
      <div className="mt-8">
        <div className="flex items-center justify-between mb-4">
          <div className="text-[13px] font-medium text-[#0B1F2D]">Recent checks</div>
          <Link to="/dashboard/history" className="text-[12px] text-[#1B6F5E] hover:underline inline-flex items-center gap-1">
            View all <ArrowUpRight className="w-3 h-3" />
          </Link>
        </div>
        <div className="rounded-2xl border border-[#E8E5DC] bg-white overflow-hidden">
          {loading ? (
            <div className="px-5 py-10 text-center text-[13px] text-[#0B1F2D]/55">Loading recent checks...</div>
          ) : recent.length === 0 ? (
            <div className="px-5 py-10 text-center text-[13px] text-[#0B1F2D]/55">
              No checks yet. <Link to="/dashboard/run" className="text-[#1B6F5E] underline">Run your first check.</Link>
            </div>
          ) : (
            <div className="divide-y divide-[#E8E5DC]">
              {recent.map((c) => {
                const gaps = (c.results_json || []).filter(r => r.outcome === 'fail').length;
                return (
                  <div key={c.check_id} className="flex items-center justify-between px-5 py-4 hover:bg-[#FAFAF5]/60 transition-colors">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-lg bg-[#FAFAF5] border border-[#E8E5DC] flex items-center justify-center text-[#0B1F2D]/50">
                        <FileText className="w-4 h-4" />
                      </div>
                      <div>
                        <div className="text-[13px] font-medium text-[#0B1F2D]">{c.entity_name}</div>
                        <div className="text-[11.5px] text-[#0B1F2D]/55">{new Date(c.created_at).toLocaleDateString()} · {c.framework_code}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className={`font-mono-tight text-[13px] font-semibold ${
                        parseFloat(c.overall_score) >= 80 ? 'text-[#1B6F5E]' : 'text-[#A4243B]'
                      }`}>
                        {parseFloat(c.overall_score).toFixed(0)}%
                      </span>
                      <span className={`text-[10.5px] font-medium px-2 py-1 rounded-full border ${
                        c.compliant
                          ? 'bg-[#E8F3EE] text-[#1B6F5E] border-[#1B6F5E]/25'
                          : 'bg-[#FBEAEA] text-[#A4243B] border-[#A4243B]/25'
                      }`}>
                        {c.compliant ? 'Compliant' : `${gaps} gaps`}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
